import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Icon } from '../ui/Icon';
import { Spinner } from '../ui/primitives';

/**
 * ExpirationField
 *
 * Compact inline field for viewing & editing a document's expiration date.
 * Saves directly to document_uploads.expires_at via Supabase.
 *
 * Props:
 *   uploadId   – document_uploads.id (required)
 *   expiresAt  – current value: 'YYYY-MM-DD' string or null
 *   canEdit    – whether to show edit affordance (default true)
 *   onChanged  – callback(newExpiresAt: string|null)
 *   variant    – 'compact' | 'inline' — controls density (default 'inline')
 */
export default function ExpirationField({
  uploadId,
  expiresAt,
  canEdit = true,
  onChanged,
  variant = 'inline',
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(expiresAt || '');
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(expiresAt || '');
  }, [expiresAt, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try { inputRef.current.showPicker?.(); } catch { /* no-op */ }
    }
  }, [editing]);

  const save = async (newValue) => {
    setSaving(true);
    setErr(null);
    try {
      const payload = newValue && newValue.length === 10 ? newValue : null;
      const { error } = await supabase
        .from('document_uploads')
        .update({ expires_at: payload })
        .eq('id', uploadId);
      if (error) throw error;
      onChanged?.(payload);
      setEditing(false);
    } catch (e) {
      console.error('Save expiration failed:', e);
      setErr(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = () => {
    if (draft === (expiresAt || '')) { setEditing(false); return; }
    save(draft);
  };

  const handleClear = () => save(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter')  handleConfirm();
    if (e.key === 'Escape') { setDraft(expiresAt || ''); setEditing(false); }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const expiry = expiresAt ? new Date(expiresAt + 'T00:00:00') : null;
  const daysUntil = expiry
    ? Math.round((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const urgency = daysUntil === null ? 'neutral'
                : daysUntil < 0     ? 'expired'
                : daysUntil <= 14   ? 'soon'
                : daysUntil <= 30   ? 'upcoming'
                : 'normal';

  const urgencyStyles = {
    neutral:  { bg: 'var(--surface-2)',  fg: 'var(--text-3)',     border: 'var(--border)' },
    normal:   { bg: 'var(--ok-bg)',      fg: 'var(--ok)',         border: 'rgba(61,199,91,0.22)' },
    upcoming: { bg: 'var(--info-bg)',    fg: 'var(--info)',       border: 'rgba(59,130,246,0.22)' },
    soon:     { bg: 'var(--warn-bg)',    fg: 'var(--warn)',       border: 'rgba(245,158,11,0.28)' },
    expired:  { bg: 'var(--danger-bg)',  fg: 'var(--danger)',     border: 'rgba(239,68,68,0.28)' },
  };
  const u = urgencyStyles[urgency];

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const urgencyLabel =
    urgency === 'expired'  ? `Expired ${Math.abs(daysUntil)}d ago`
  : urgency === 'soon'     ? `In ${daysUntil}d`
  : urgency === 'upcoming' ? `In ${daysUntil}d`
  : null;

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <input
          ref={inputRef}
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={saving}
          style={{
            fontSize: variant === 'compact' ? 11.5 : 12.5,
            padding: variant === 'compact' ? '4px 8px' : '6px 10px',
            border: '1px solid var(--accent)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--surface-1)',
            color: 'var(--text)',
            outline: 'none',
            fontFamily: 'inherit',
            minWidth: 132,
          }}
        />
        <button
          type="button"
          onClick={handleConfirm}
          disabled={saving}
          title="Save"
          style={{ ...iconBtnStyle, color: 'var(--ok)', background: 'var(--ok-bg)', borderColor: 'rgba(61,199,91,0.22)' }}
        >
          {saving ? <Spinner size={11} color="var(--ok)" /> : <Icon.check size={12} />}
        </button>
        <button
          type="button"
          onClick={() => { setDraft(expiresAt || ''); setEditing(false); setErr(null); }}
          disabled={saving}
          title="Cancel"
          style={iconBtnStyle}
        >
          <Icon.x size={12} />
        </button>
        {expiresAt && (
          <button
            type="button"
            onClick={handleClear}
            disabled={saving}
            title="Remove expiration"
            style={{ ...iconBtnStyle, color: 'var(--danger)', background: 'var(--danger-bg)', borderColor: 'rgba(239,68,68,0.18)' }}
          >
            <Icon.trash size={12} />
          </button>
        )}
        {err && <span style={{ fontSize: 11, color: 'var(--danger)' }}>{err}</span>}
      </div>
    );
  }

  if (!expiresAt) {
    if (!canEdit) return null;
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: variant === 'compact' ? '3px 8px' : '4px 10px',
          fontSize: variant === 'compact' ? 11 : 11.5,
          fontWeight: 500,
          color: 'var(--text-3)',
          background: 'transparent',
          border: '1px dashed var(--border)',
          borderRadius: 999,
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'all 0.12s',
          minHeight: variant === 'compact' ? 0 : undefined,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--accent)';
          e.currentTarget.style.color = 'var(--accent)';
          e.currentTarget.style.background = 'var(--accent-bg, rgba(63,197,107,0.08))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.color = 'var(--text-3)';
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <Icon.calendar size={12} />
        Add expiration
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => canEdit && setEditing(true)}
      disabled={!canEdit}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: variant === 'compact' ? '3px 8px' : '4px 10px',
        fontSize: variant === 'compact' ? 11 : 11.5,
        fontWeight: 600,
        color: u.fg,
        background: u.bg,
        border: `1px solid ${u.border}`,
        borderRadius: 999,
        cursor: canEdit ? 'pointer' : 'default',
        fontFamily: 'inherit',
        transition: 'all 0.12s',
        fontVariantNumeric: 'tabular-nums',
        // Override the global 44px mobile touch floor — this is small
        // inline metadata, not a primary action.
        minHeight: variant === 'compact' ? 0 : undefined,
      }}
      title={canEdit ? 'Click to edit expiration date' : `Expires ${fmtDate(expiresAt)}`}
    >
      <Icon.calendar size={11} />
      {fmtDate(expiresAt)}
      {urgencyLabel && (
        <span style={{
          fontSize: 10, fontWeight: 700, opacity: 0.85,
          paddingLeft: 4, borderLeft: `1px solid ${u.border}`,
          marginLeft: 2,
        }}>
          {urgencyLabel}
        </span>
      )}
    </button>
  );
}

const iconBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  minHeight: 24,
  padding: 0,
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--border)',
  background: 'var(--surface-1)',
  color: 'var(--text-2)',
  cursor: 'pointer',
  fontFamily: 'inherit',
};
